import type { EditorAction, EditorState } from "../editor/reducers";
import type { RegionsToolOverlayFragment } from "../client/generated";
import { useState } from "react";
import { CSS_IN_TO_PX, toPercent } from "../editor/utils";
import { ReshapeableRegion } from "./ReshapeableRegion";
import { TransformableRegion } from "./TransformableRegion";
import { RegionCanvas } from "./RegionCanvas";
import { useCreateRegionMutation } from "../client/generated";

type Props = {
  state: EditorState;
  fragment: RegionsToolOverlayFragment;
  dispatch: React.Dispatch<EditorAction>;
};

type Point = {
  x: number;
  y: number;
};

export function RegionsToolOverlay({ state, fragment, dispatch }: Props) {
  const pageWidth = fragment.width * CSS_IN_TO_PX;
  const pageHeight = fragment.height * CSS_IN_TO_PX;
  const [startCoords, setStartCoords] = useState<Point>();
  const [endCoords, setEndCoords] = useState<Point>();

  const [createRegion] = useCreateRegionMutation({
    refetchQueries: "active",
  });

  const onMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    dispatch({ type: "deselectRegion" });
    setEndCoords(undefined);
    setStartCoords({
      x: event.nativeEvent.offsetX / pageWidth,
      y: event.nativeEvent.offsetY / pageHeight,
    });
  };

  const onMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!startCoords || event.buttons !== 1) return;
    setEndCoords({
      x: event.nativeEvent.offsetX / pageWidth,
      y: event.nativeEvent.offsetY / pageHeight,
    });
  };

  const onMouseUp = async () => {
    setStartCoords(undefined);
    setEndCoords(undefined);

    if (!startCoords || !endCoords) return;
    if (startCoords.x === endCoords.x || startCoords.y === endCoords.y) return;

    const { data, errors } = await createRegion({
      variables: {
        pageId: fragment.id,
        vertices: [
          {
            left: Math.min(startCoords.x, endCoords.x),
            top: Math.min(startCoords.y, endCoords.y),
          },
          {
            left: Math.min(startCoords.x, endCoords.x),
            top: Math.max(startCoords.y, endCoords.y),
          },
          {
            left: Math.max(startCoords.x, endCoords.x),
            top: Math.max(startCoords.y, endCoords.y),
          },
          {
            left: Math.max(startCoords.x, endCoords.x),
            top: Math.min(startCoords.y, endCoords.y),
          },
        ],
      },
    });

    if (errors) {
      console.log("Error creating region", errors);
    } else if (data?.createRegion.userErrors) {
      console.log("User error creating region", data.createRegion.userErrors);
    }
  };

  const regionsSortedByFocusPriority = fragment.regions.slice().sort((a, b) => {
    const areaA = a.shape.aabb.height * a.shape.aabb.width;
    const areaB = b.shape.aabb.height * b.shape.aabb.width;
    return areaB - areaA;
  });

  return (
    <div
      className="absolute inset-0"
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    >
      {state.mode === "rectangle" &&
        regionsSortedByFocusPriority.map((region) => (
          <TransformableRegion
            key={region.id}
            fragment={region}
            isSelected={state.selectedRegionId === region.id}
            dispatch={dispatch}
          />
        ))}

      {state.mode === "polygon" &&
        regionsSortedByFocusPriority.map((region) => (
          <ReshapeableRegion
            key={region.id}
            fragment={region}
            isSelected={state.selectedRegionId === region.id}
            dispatch={dispatch}
          />
        ))}

      <RegionCanvas>
        {startCoords && endCoords && (
          <rect
            className="pointer-events-auto"
            x={toPercent(Math.min(startCoords.x, endCoords.x))}
            y={toPercent(Math.min(startCoords.y, endCoords.y))}
            width={toPercent(Math.abs(endCoords.x - startCoords.x))}
            height={toPercent(Math.abs(endCoords.y - startCoords.y))}
            stroke="var(--color-brand)"
            fill="transparent"
          />
        )}
      </RegionCanvas>
    </div>
  );
}
